import { getScorePercentage } from '@/domains/scoring';
import {
  ApiManagedUserScoreProfile,
  ApiManagedUserScoreProfileSession,
  IManagedUserSession,
  IManagedUserSummary,
  TSimulationType,
} from '@/types';

/**
 * Maps API response data for a single managed user's score profile into the
 * frontend-friendly summary format.
 *
 * This function transforms the API payload used by the managed-user dashboard
 * into the `IManagedUserSummary` model consumed by frontend components.
 */
export const mapApiManagedUserDataToFrontend = (
  data: ApiManagedUserScoreProfile,
): IManagedUserSummary => {
  /**
   * Key mapping responsibilities:
   * 1. Convert API field names (e.g. `user_id`) to frontend conventions (e.g. `userId`).
   * 2. Normalize nullable top-level identity fields (`user_name`, `user_email`, `user_role`)
   *    to safe defaults for UI rendering.
   * 3. Map certification metadata into the frontend certification shape.
   * 4. Map each session array (`certificates`, `practice_certificates`, `roleplays`) into
   *    `IManagedUserSession[]` using a shared helper for consistent transformation.
   * 5. Apply defensive fallback defaults for potentially missing arrays to ensure stable
   *    frontend models (`[]` instead of `null`/`undefined`).
   * 6. Return a fully normalized `IManagedUserSummary` object ready for store/state usage.
   */
  return {
    userId: data?.user_id,
    userName: data?.user_name || '',
    userEmail: data?.user_email || '',
    userRole: data?.user_role || '',
    certifications: mapApiCertificationProducts(data) || [],
    certificates: data?.certificates?.map((cert) => mapApiSession(cert)) || [],
    practiceCertificates: data?.practice_certificates?.map((cert) => mapApiSession(cert)) || [],
    roleplays: data?.roleplays?.map((roleplay) => mapApiSession(roleplay)) || [],
  };
};

/**
 * Maps API certification metadata to the frontend-friendly certification format.
 * This helper focuses on transforming the `certifications` array from the API profile
 * into the shape expected by the frontend summary, ensuring consistent field naming and
 * handling nullable short titles.
 *
 * @param data - The API managed user score profile containing certification metadata to map
 * @returns The mapped array of frontend-friendly certification objects
 */
function mapApiCertificationProducts(
  data: ApiManagedUserScoreProfile,
): IManagedUserSummary['certifications'] {
  return (
    data?.products?.map((product) => ({
      certificationId: product?.product_id,
      certificationTitle: product?.product_name,
      certificationTitleShort: product?.product_name_short || null,
    })) || []
  );
}

/**
 * Helper function to map a managed-user score profile session to the frontend
 * `IManagedUserSession` format.
 *
 * @param session - The API session object to map
 * @returns The mapped `IManagedUserSession`
 */
function mapApiSession(session: ApiManagedUserScoreProfileSession): IManagedUserSession {
  // Map API session fields to frontend naming and normalize nullable values where needed.
  // - Preserve numeric identifiers and titles for dashboard display.
  // - Normalize nullable attempt, pass/fail, and score max values for predictable rendering.
  // - Cast `session_type` to `TSimulationType`, assuming API values align with frontend expectations.
  return {
    attemptCount: session?.attempt_count || null,
    attemptNumber: session?.attempt_number || null,
    certificationId: session?.product_id,
    certificationTitle: session?.product_name,
    certificationTitleShort: session?.product_name_short || null,
    passed: session?.passed || null,
    score: session?.total_score || 0,
    scoreMax: session?.score_max || null,
    scorePercentage: getScorePercentage(session),
    sessionId: session?.session_id,
    simulationId: session?.scenario_id,
    simulationTitle: session?.scenario_title,
    simulationTitleShort: session?.scenario_title_short || null,
    simulationType: session?.session_type as TSimulationType,
    submittedAt: session?.submitted_at,
  };
}
